import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, AlertTriangle } from 'lucide-react';

// Session timeout settings (in milliseconds)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Show warning 5 minutes before timeout

export const SessionManager = ({ children }) => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Hide warning if shown
    setShowWarning(false);

    if (!user) return;

    // Set warning timer (shows modal before session expires)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.floor(WARNING_BEFORE_TIMEOUT / 1000));
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      setShowWarning(false);
      logout();
    }, SESSION_TIMEOUT);
  }, [user, logout]);

  // Handle user staying active
  const handleStayActive = () => {
    resetTimer();
    setShowWarning(false);
  };

  // Handle user choosing to logout
  const handleLogoutNow = () => {
    setShowWarning(false);
    logout();
  };

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle the reset to avoid too many calls
    let lastReset = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset > 60000) { // Only reset every minute at most
        lastReset = now;
        resetTimer();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [user, resetTimer]);

  // Format countdown for display
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      
      <Dialog open={showWarning} onOpenChange={() => {}}>
        <DialogContent className="bg-[#1A1A2E] border-[#2A2A4A] text-white max-w-md" hideCloseButton>
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <DialogTitle className="text-xl">Session Expiring Soon</DialogTitle>
            <DialogDescription className="text-[#8888AA]">
              Your session will expire due to inactivity.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm">Logging out in</span>
            </div>
            <div className="text-5xl font-bold text-white mb-4 font-mono">
              {formatCountdown(countdown)}
            </div>
            <p className="text-[#8888AA] text-sm">
              Click "Stay Logged In" to continue your session.
            </p>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              onClick={handleLogoutNow}
              variant="outline"
              className="flex-1 border-[#2A2A4A] text-[#0A0F1E] hover:bg-[#16213E] rounded-xl"
            >
              Log Out Now
            </Button>
            <Button
              onClick={handleStayActive}
              className="flex-1 bg-[#E94560] hover:bg-[#E94560]/90 text-white rounded-xl"
              data-testid="stay-logged-in-btn"
            >
              Stay Logged In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SessionManager;
